import type { Tone } from "@/components/StatusPill";
import { type ReturnState } from "@/lib/api";

const lookup = (table: Record<string, Tone>, fallback: Tone) => (status: string): Tone =>
  table[status] ?? fallback;

/** Pool tag lifecycle (tags page). */
export const poolTagTone = lookup(
  { inventory: "info", unassigned: "warning", assigned: "success", paused: "neutral", revoked: "danger" },
  "neutral",
);

/** Stripe tag-order lifecycle (tag orders page). */
export const tagOrderTone = lookup(
  { paid: "success", refunded: "danger", abandoned: "neutral", payment_init_failed: "neutral" },
  "info",
);

/** Carrier postal reservation lifecycle (postal page). */
export const postalReservationTone = lookup(
  { awaiting_dropoff: "warning", dropped_off: "info", in_transit: "info", out_for_delivery: "info", delivered: "success", expired: "danger" },
  "neutral",
);

/** Launch waitlist subscriber status. */
export const launchSubscriberTone = lookup({ notified: "success", unsubscribed: "neutral" }, "info");

/** Promo code lifecycle (promo codes page). */
export const promoCodeTone = lookup({ active: "success", disabled: "neutral" }, "neutral");

/** Item lifecycle on the user detail page. */
export const itemStatusTone = lookup({ lost: "warning", found: "info", returned: "success" }, "neutral");

/**
 * returnRequests lifecycle (returns page). Typed `Record<ReturnState, Tone>`
 * — exhaustive over the shared `ReturnState` union (lib/api.ts), so adding a
 * new state there without a tone here is a compile error, not a silent
 * "neutral" fallback.
 */
const RETURN_STATE_TONE: Record<ReturnState, Tone> = {
  awaiting_collection: "warning",
  pending_owner_acceptance: "warning",
  awaiting_payment: "warning",
  awaiting_finder_service_point_selection: "info",
  creating_reservation: "info",
  awaiting_dropoff: "info",
  dropped_off: "info",
  in_transit: "info",
  out_for_delivery: "info",
  delivered: "success",
  completed: "success",
  declined: "neutral",
  failed: "danger",
  expired: "danger",
};
export function returnStateTone(state: string): Tone {
  return (RETURN_STATE_TONE as Record<string, Tone>)[state] ?? "neutral";
}

/** Referral row lifecycle (referrals page). */
export const referralTone = lookup(
  { registered: "neutral", verified: "info", review: "warning", credited: "success", awarded: "success", rejected: "danger", clawed_back: "danger" },
  "neutral",
);
